"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { cn } from "@/utilities/core/cn";
import { useLocale } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { mapTenantToFUWidgetTheme, resolveThemeMode } from "@/features/theme/fu-widget-theme";
import { getWidgetLanguage } from "@/lib/labels/widgetLanguage";
const EventCard = dynamic(() => import("fansunited-frontend-components").then(m => ({
  default: m.EventCard
})), {
  ssr: false
});
type EventCardTemplate = "standard" | "hero" | "compact";

/**
 * Maps the CMS `variant` field to an EventCard template.
 * `detailed` maps to `"hero"` for now — swap to `"detailed"` once EventCard ships that template.
 */
function variantToTemplate(variant: "header" | "detailed"): EventCardTemplate {
  return variant === "detailed" ? "hero" : "standard";
}
interface EventCardWrapperProps {
  matchId: string;
  variant: "header" | "detailed";
  linkUrl?: string;
  className?: string;
}
export function EventCardWrapper(t0) {
  const $ = _c(35);
  const {
    matchId,
    variant,
    linkUrl,
    className
  } = t0;
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setMounted(true);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const sdk = useFansUnitedSDK();
  const tenantSettings = useTenantSettings();
  const locale = useLocale();
  let t3;
  if ($[2] !== tenantSettings) {
    t3 = mapTenantToFUWidgetTheme(tenantSettings);
    $[2] = tenantSettings;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const base = t3;
  let t4;
  if ($[4] !== tenantSettings) {
    t4 = resolveThemeMode(tenantSettings);
    $[4] = tenantSettings;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== base.colorSchemes.light) {
    t5 = {
      ...base.colorSchemes.light,
      onSurface: base.colorSchemes.light.surfaceVariant
    };
    $[6] = base.colorSchemes.light;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== base.colorSchemes || $[9] !== t5) {
    t6 = {
      ...base.colorSchemes,
      light: t5
    };
    $[8] = base.colorSchemes;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== base || $[12] !== t4 || $[13] !== t6) {
    t7 = {
      ...base,
      mode: t4,
      colorSchemes: t6
    };
    $[11] = base;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const themeOptions = t7;
  let t8;
  if ($[15] !== locale) {
    t8 = getWidgetLanguage(locale);
    $[15] = locale;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const language = t8;
  let t9;
  if ($[17] !== variant) {
    t9 = variantToTemplate(variant);
    $[17] = variant;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const template = t9;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="animate-pulse rounded-lg bg-muted/30 p-6 h-32" role="status"><span className="sr-only">Loading match...</span></div>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const skeleton = t10;
  let t11;
  if ($[20] !== language || $[21] !== matchId || $[22] !== mounted || $[23] !== sdk || $[24] !== template || $[25] !== themeOptions) {
    t11 = !mounted || !sdk ? skeleton : <EventCard eventId={matchId} sdk={sdk} language={language} template={template} themeOptions={themeOptions as unknown as Record<string, unknown>} />;
    $[20] = language;
    $[21] = matchId;
    $[22] = mounted;
    $[23] = sdk;
    $[24] = template;
    $[25] = themeOptions;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const card = t11;
  let t12;
  if ($[27] !== className) {
    t12 = cn("not-prose mb-6 text-[15px]", className);
    $[27] = className;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== card || $[30] !== linkUrl) {
    t13 = linkUrl ? <a href={linkUrl} className="block cursor-pointer">{card}</a> : card;
    $[29] = card;
    $[30] = linkUrl;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t12 || $[33] !== t13) {
    t14 = <section className={t12}>{t13}</section>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}